import { useEffect, useRef, useState } from "react";
import { MessageCircle, QrCode, CheckSquare, Square, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const STATUS_LABEL = { pendiente: "Pendiente", importando: "Importando...", importado: "Importado", error: "Error" };
const ROLE_LABEL = { cliente: "Cliente", proveedor: "Proveedor", desconocido: "—" };

const normalizeSearch = (s) =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Onboarding de migración de Fase A: cada usuario (dueño u operario) vincula acá SU PROPIO
// WhatsApp personal, elige qué charlas aporta, y se desvincula al terminar. Se usa tanto en
// el panel del dueño como en la vista del operario — self-service, cada uno gestiona la suya.
export default function WhatsAppMigrationWidget({ hideWhenClosed = false } = {}) {
  const [state, setState] = useState(null); // {instance, chats}
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [rawChats, setRawChats] = useState(null);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [roles, setRoles] = useState({}); // phone -> "cliente" | "proveedor", elegido a mano en el checklist
  const [chatSearch, setChatSearch] = useState("");
  const [enabling, setEnabling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);
  const qrPollRef = useRef(null);
  const dataPollRef = useRef(null);
  const connPollRef = useRef(null);

  const load = () => api.get("/whatsapp/migration/instances/me").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const inst = state?.instance;
  const chats = state?.chats || [];
  const hasPending = chats.some((c) => ["pendiente", "importando"].includes(c.import_status));

  useEffect(() => {
    clearInterval(dataPollRef.current);
    if (!inst || inst.status === "cerrada") return;
    if (inst.status === "conectado" && !hasPending) return;
    dataPollRef.current = setInterval(load, 4000);
    return () => clearInterval(dataPollRef.current);
  }, [inst?.status, hasPending]); // eslint-disable-line

  // Una vez "conectado" no queda nada más pendiente que el poll de arriba siga chequeando —
  // pero eso NO detecta que la persona cerró sesión de WhatsApp desde su celular (o que
  // Evolution perdió la sesión) después. Sin este chequeo aparte, el panel se queda mostrando
  // "Conectado" para siempre aunque ya no llegue nada nuevo.
  useEffect(() => {
    clearInterval(connPollRef.current);
    if (!inst || inst.status !== "conectado") return;
    const checkConnection = () => {
      api.get(`/whatsapp/migration/instances/${inst.id}/status`).then(({ data }) => {
        if (data.state !== "open") load();
      }).catch(() => {});
    };
    checkConnection();
    connPollRef.current = setInterval(checkConnection, 20000);
    return () => clearInterval(connPollRef.current);
  }, [inst?.id, inst?.status]); // eslint-disable-line

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/whatsapp/migration/instances", {});
      setState({ instance: data, chats: [] });
      openQr(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setStarting(false); }
  };

  const fetchQr = async (id) => {
    setLoadingQr(true);
    try {
      const { data } = await api.get(`/whatsapp/migration/instances/${id}/qrcode`);
      setQr(data.base64);
    } catch (e) { toast.error(apiError(e)); setShowQr(false); } finally { setLoadingQr(false); }
  };

  const openQr = (id) => { setQr(null); setShowQr(true); fetchQr(id || inst.id); };

  useEffect(() => {
    if (!showQr || !inst) { clearInterval(qrPollRef.current); return; }
    qrPollRef.current = setInterval(async () => {
      const { data } = await api.get(`/whatsapp/migration/instances/${inst.id}/status`).catch(() => ({ data: {} }));
      if (data.state === "open") {
        clearInterval(qrPollRef.current);
        setShowQr(false);
        toast.success("¡Tu WhatsApp quedó vinculado!");
        load();
      }
    }, 4000);
    return () => clearInterval(qrPollRef.current);
  }, [showQr, inst?.id]); // eslint-disable-line

  const loadRawChats = async () => {
    setLoadingRaw(true);
    try {
      const { data } = await api.get(`/whatsapp/migration/instances/${inst.id}/chats`);
      setRawChats(data);
      setSelected(new Set(data.filter((c) => c.already_enabled).map((c) => c.phone)));
      const initialRoles = {};
      data.forEach((c) => {
        const already = chats.find((x) => x.phone === c.phone);
        initialRoles[c.phone] = ["cliente", "proveedor"].includes(already?.detected_role)
          ? already.detected_role : "cliente";
      });
      setRoles(initialRoles);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingRaw(false); }
  };

  const toggle = (phone) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  };

  const setRole = (phone, role) => {
    setRoles((prev) => ({ ...prev, [phone]: role }));
    setSelected((prev) => new Set(prev).add(phone));
  };

  const enableSelected = async () => {
    if (selected.size === 0) { toast.error("Elegí al menos una charla"); return; }
    setEnabling(true);
    try {
      const chat_labels = {};
      const chosen_roles = {};
      const jids = {};
      (rawChats || []).forEach((c) => { chat_labels[c.phone] = c.name; if (c.jid) jids[c.phone] = c.jid; });
      selected.forEach((phone) => { chosen_roles[phone] = roles[phone] || "cliente"; });
      const { data } = await api.post(`/whatsapp/migration/instances/${inst.id}/enable`,
        { phones: Array.from(selected), chat_labels, roles: chosen_roles, jids });
      if (data.enabled > 0) toast.success(`Aportando ${data.enabled} charla(s)...`);
      if (data.skipped_already_migrated?.length) {
        toast.info(`${data.skipped_already_migrated.length} charla(s) ya la(s) habías aportado antes, no se repiten.`);
      }
      setRawChats(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setEnabling(false); }
  };

  const closeInstance = async () => {
    setClosing(true);
    try {
      const { data } = await api.post(`/whatsapp/migration/instances/${inst.id}/close`);
      setState((s) => ({ ...s, instance: data }));
      toast.success("Listo, tu WhatsApp quedó desvinculado.");
    } catch (e) { toast.error(apiError(e)); } finally { setClosing(false); }
  };

  const reclassify = async (chatId, role) => {
    try { await api.patch(`/whatsapp/migration/chats/${chatId}/reclassify`, { role }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filteredRawChats = (() => {
    const q = normalizeSearch(chatSearch);
    if (!q) return rawChats || [];
    return (rawChats || []).filter((c) => normalizeSearch(c.name).includes(q) || c.phone.includes(q));
  })();

  if (!state) return null;
  if (hideWhenClosed && inst?.status === "cerrada") return null;

  return (
    <div className="bg-[#171717] border border-neutral-800 rounded-md p-5" data-testid="wa-migration-widget">
      <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-neutral-300 flex items-center gap-2 mb-3">
        <MessageCircle className="w-4 h-4 text-[#F2B90D]" /> Migrá tu WhatsApp personal
      </h2>

      {!inst && (
        <div className="space-y-2">
          <p className="text-xs text-neutral-400">
            Vinculá tu WhatsApp para elegir qué charlas de clientes o proveedores aportar a la empresa.
            Cuando termines, te desvinculás y dejás de depender de tu celular.
          </p>
          <Button onClick={start} disabled={starting}
            className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="wa-mig-start-btn">
            {starting ? "Creando..." : "Vincular mi WhatsApp"}
          </Button>
        </div>
      )}

      {inst && inst.status !== "cerrada" && inst.status !== "conectado" && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-neutral-400">
            {inst.connected_at
              ? "Se desconectó tu WhatsApp (¿cerraste sesión desde el celular?). Volvé a escanear el código QR para seguir — no perdiste lo que ya aportaste."
              : "Todavía no escaneaste el código QR con tu WhatsApp."}
          </p>
          <Button size="sm" onClick={() => openQr()}
            className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="wa-mig-qr-btn">
            <QrCode className="w-3.5 h-3.5 mr-1" /> Ver código QR
          </Button>
        </div>
      )}

      {inst && inst.status === "conectado" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-green-400">Conectado{inst.phone ? ` (${inst.phone})` : ""}. Elegí qué charlas aportar y marcá si cada una es cliente o proveedor.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadRawChats} disabled={loadingRaw}
                className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-mig-load-chats-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> {rawChats ? "Actualizar charlas" : "Ver mis charlas"}
              </Button>
              <Button size="sm" variant="outline" onClick={closeInstance} disabled={closing}
                className="border-red-900 text-red-400 rounded-sm text-xs hover:bg-red-950" data-testid="wa-mig-close-btn">
                <LogOut className="w-3.5 h-3.5 mr-1" /> Terminar y desvincular
              </Button>
            </div>
          </div>

          {rawChats && rawChats.length > 0 && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={chatSearch} onChange={(e) => setChatSearch(e.target.value)}
                placeholder="Buscar por nombre o número..."
                className="bg-neutral-900 border-neutral-800 rounded-sm pl-9 h-9 text-sm" data-testid="wa-mig-chat-search" />
            </div>
          )}

          {rawChats && (
            <div className="border border-neutral-800 rounded-md divide-y divide-neutral-800 max-h-72 overflow-y-auto scroll-visible">
              {rawChats.length === 0 && <p className="text-xs text-neutral-500 p-4">No encontramos charlas en tu WhatsApp.</p>}
              {rawChats.length > 0 && filteredRawChats.length === 0 && (
                <p className="text-xs text-neutral-500 p-4">Nada coincide con "{chatSearch}".</p>
              )}
              {filteredRawChats.map((c) => {
                const isSel = selected.has(c.phone);
                const role = roles[c.phone] || "cliente";
                return (
                  <div key={c.phone} className="px-3 py-2 flex items-center gap-3 hover:bg-neutral-800/60">
                    <button onClick={() => toggle(c.phone)} className="shrink-0" data-testid={`wa-mig-chat-${c.phone}`}>
                      {isSel
                        ? <CheckSquare className="w-4 h-4 text-[#F2B90D]" />
                        : <Square className="w-4 h-4 text-neutral-500" />}
                    </button>
                    <button onClick={() => toggle(c.phone)} className="min-w-0 flex-1 text-left">
                      <p className="text-sm truncate">{c.name}</p>
                      {c.last_preview && <p className="text-xs text-neutral-500 truncate">{c.last_preview}</p>}
                    </button>
                    {isSel && (
                      <div className="flex gap-1 shrink-0" data-testid={`wa-mig-role-${c.phone}`}>
                        <button onClick={() => setRole(c.phone, "cliente")}
                          className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm border transition-colors ${role === "cliente" ? "bg-green-500/20 border-green-500/50 text-green-400" : "border-neutral-700 text-neutral-500 hover:text-white"}`}
                          data-testid={`wa-mig-role-cliente-${c.phone}`}>
                          Cliente
                        </button>
                        <button onClick={() => setRole(c.phone, "proveedor")}
                          className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm border transition-colors ${role === "proveedor" ? "bg-blue-500/20 border-blue-500/50 text-blue-400" : "border-neutral-700 text-neutral-500 hover:text-white"}`}
                          data-testid={`wa-mig-role-proveedor-${c.phone}`}>
                          Proveedor
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {rawChats && rawChats.length > 0 && (
            <Button onClick={enableSelected} disabled={enabling || selected.size === 0}
              className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="wa-mig-enable-btn">
              {enabling ? "Aportando..." : `Aportar ${selected.size} charla(s) seleccionada(s)`}
            </Button>
          )}

          {chats.length > 0 && (
            <div>
              <p className="text-[11px] uppercase font-bold text-neutral-500 mb-2">Charlas aportadas</p>
              <div className="space-y-1.5">
                {chats.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 text-xs bg-neutral-900 rounded-sm px-3 py-2" data-testid={`wa-mig-enabled-${c.phone}`}>
                    <span className="truncate">{c.chat_label || c.phone}</span>
                    {c.import_status === "importado" ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="uppercase font-bold text-green-400">
                          {ROLE_LABEL[c.detected_role]}{c.has_active_job ? " · Trabajo" : ""}
                        </span>
                        <button onClick={() => reclassify(c.id, c.detected_role === "proveedor" ? "cliente" : "proveedor")}
                          className="text-neutral-500 hover:text-white underline normal-case" data-testid={`wa-mig-reclassify-${c.phone}`}>
                          {c.detected_role === "proveedor" ? "Marcar cliente" : "Marcar proveedor"}
                        </button>
                      </div>
                    ) : (
                      <span className={`uppercase font-bold shrink-0 ${c.import_status === "error" ? "text-red-400" : "text-[#F2B90D]"}`}>
                        {c.import_status === "importando" && <Loader2 className="w-3 h-3 inline animate-spin mr-1" />}
                        {STATUS_LABEL[c.import_status]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {inst && inst.status === "cerrada" && (
        <div className="text-xs text-neutral-400 space-y-2">
          <p className="text-green-400 font-bold uppercase">Ya te desvinculaste — ¡gracias por aportar!</p>
          <p>{inst.stats.clients_detected} cliente(s), {inst.stats.providers_detected} proveedor(es) y {inst.stats.jobs_detected} trabajo(s) en curso detectados.</p>
          <p>¿Te acordaste de otra charla, o sigue llegando algo nuevo de un cliente? Podés volver a vincular tu WhatsApp cuando quieras.</p>
          <Button size="sm" onClick={start} disabled={starting}
            className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="wa-mig-restart-btn">
            {starting ? "Vinculando..." : "Vincular de nuevo"}
          </Button>
        </div>
      )}

      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">Vincular tu WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            <p className="text-xs text-neutral-400">
              Abrí WhatsApp en TU celular → Dispositivos vinculados → Vincular un dispositivo, y escaneá este código.
            </p>
            <div className="bg-white rounded-md p-4 flex items-center justify-center min-h-[280px]">
              {loadingQr && <p className="text-neutral-500 text-sm">Generando código...</p>}
              {!loadingQr && qr && <img src={qr} alt="Código QR de WhatsApp" className="w-64 h-64" data-testid="wa-mig-qr-image" />}
            </div>
            <p className="text-[11px] text-neutral-500">El código expira rápido — si se vence, pedí uno nuevo.</p>
            <Button variant="outline" onClick={() => fetchQr(inst.id)} disabled={loadingQr}
              className="w-full border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-mig-qr-refresh">
              Pedir código nuevo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
